import styled from "@emotion/styled";
import { DelegateButton } from "@/pages/agent/delegation/delegate-button";

import {
  MDSActionMenuDropdown,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuDropdownSlotName,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import type { TaskRowViewModel } from "@/pages/agent/tasks/agent-tasks-page.utils";
import { formatAgentDate, getAgentDateTime } from "@/pages/agent/utils/agent-date.utils";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";
import { formatStatusLabel } from "@/pages/agent/utils/agent-status.utils";
import { normalizeAssistantTaskStatus } from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentTaskListItem = ({
  taskRow,
  showProject = true,
  onOpenActivities,
  onOpenNote,
  onOpenProject,
  onOpenTask,
  onToggleTaskComplete,
}: {
  taskRow: TaskRowViewModel;
  showProject?: boolean;
  onOpenActivities: ({ taskId }: { taskId: string }) => void;
  onOpenNote: ({ noteId }: { noteId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
}) => {
  const project = taskRow.project;
  const sourceNote = taskRow.sourceNote;
  const taskTitle = decodeAgentDisplayText({ value: taskRow.task.title || "Untitled task" });
  const projectTitle = decodeAgentDisplayText({ value: taskRow.projectTitle });
  const sourceNoteTitle = sourceNote
    ? decodeAgentDisplayText({ value: sourceNote.title || "Untitled note" })
    : null;
  const normalizedStatus = normalizeAssistantTaskStatus({ status: taskRow.task.status });
  const toggleAriaLabel = (() => {
    if (normalizedStatus === AssistantTaskStatus.Canceled) {
      return `Reopen ${taskTitle}`;
    }

    if (normalizedStatus === AssistantTaskStatus.Completed) {
      return `Mark ${taskTitle} incomplete`;
    }

    return `Mark ${taskTitle} complete`;
  })();
  const timingLabel = getTaskTimingLabel({ taskRow });
  const activityMenu = [
    {
      id: "view-activity-log",
      kind: MDSMenuContentItemKind.Item,
      label: "View activity log",
      icon: MDSIconKind.History,
      onClick: () => onOpenActivities({ taskId: taskRow.task.id }),
    },
  ];
  const openTask = () => onOpenTask({ taskId: taskRow.task.id });

  return (
    <StyledTaskRow
      aria-label={`Open ${taskTitle}`}
      role="listitem"
      tabIndex={0}
      onClick={openTask}
      onKeyDown={event => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openTask();
        }
      }}
    >
      <StyledCompletionBoundary onClick={event => event.stopPropagation()}>
        <MDSIconButton
          aria-label={toggleAriaLabel}
          iconKind={() => <AgentTaskStateIcon status={taskRow.task.status} />}
          size={MDSIconButtonSize.XSmall}
          variant={MDSIconButtonVariant.Transparent}
          onPress={() => onToggleTaskComplete({ taskId: taskRow.task.id })}
        />
      </StyledCompletionBoundary>

      {normalizedStatus === AssistantTaskStatus.Pending ? (
        <DelegateButton onPress={openTask} />
      ) : null}
      <StyledTaskContent>
        <StyledTaskTitle
          color={MDSTextColor.Contrast}
          variant={MDSTextVariant.TextSm}
          weight={MDSTextWeight.Medium}
        >
          {taskTitle}
        </StyledTaskTitle>
        <StyledMetadataLine>
          <StyledMetadataText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
            {formatStatusLabel({ status: taskRow.task.status })}
          </StyledMetadataText>
          {showProject ? (
            <>
              <StyledMetadataSeparator aria-hidden="true">·</StyledMetadataSeparator>
              {project ? (
                <StyledLinkBoundary onClick={event => event.stopPropagation()}>
                  <StyledMetadataLink onClick={() => onOpenProject({ projectId: project.id })}>
                    {projectTitle}
                  </StyledMetadataLink>
                </StyledLinkBoundary>
              ) : (
                <StyledMetadataText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                  {projectTitle}
                </StyledMetadataText>
              )}
            </>
          ) : null}
          {sourceNote ? (
            <>
              <StyledMetadataSeparator aria-hidden="true">·</StyledMetadataSeparator>
              <StyledLinkBoundary onClick={event => event.stopPropagation()}>
                <StyledMetadataLink onClick={() => onOpenNote({ noteId: sourceNote.id })}>
                  {sourceNoteTitle}
                </StyledMetadataLink>
              </StyledLinkBoundary>
            </>
          ) : null}
        </StyledMetadataLine>
      </StyledTaskContent>

      <StyledTrailingActions>
        {timingLabel ? (
          <StyledTimingLabel color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
            {timingLabel}
          </StyledTimingLabel>
        ) : null}
        <StyledMenuBoundary onClick={event => event.stopPropagation()}>
          <MDSActionMenuDropdown contentList={activityMenu} placement="bottom right">
            <MDSIconButton
              aria-label={`More actions for ${taskTitle}`}
              iconKind={MDSIconKind.More}
              size={MDSIconButtonSize.XSmall}
              slot={MDSMenuDropdownSlotName.Trigger}
              variant={MDSIconButtonVariant.Transparent}
              rounded
            />
          </MDSActionMenuDropdown>
        </StyledMenuBoundary>
      </StyledTrailingActions>
    </StyledTaskRow>
  );
};

const getTaskTimingLabel = ({ taskRow }: { taskRow: TaskRowViewModel }) => {
  if (taskRow.task.dueAt) {
    const dueAt = getAgentDateTime({ value: taskRow.task.dueAt });
    const prefix = dueAt && !taskRow.isClosed && dueAt.toMillis() < Date.now() ? "Overdue" : "Due";

    return `${prefix} ${formatAgentDate({ value: taskRow.task.dueAt, includeTime: true })}`;
  }

  if (!taskRow.isClosed && taskRow.nextFollowUp) {
    return `Reminder ${formatAgentDate({
      value: taskRow.nextFollowUp.scheduledFor ?? taskRow.nextFollowUp.dueAt,
      includeTime: true,
    })}`;
  }

  return null;
};

const StyledTaskRow = styled.div`
  align-items: center;
  background: transparent;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.surface};
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: -2px;
  }
`;

const StyledCompletionBoundary = styled.div`
  flex: 0 0 auto;
`;

const StyledTaskContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledTaskTitle = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledMetadataLine = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledMetadataText = styled(MDSText)`
  min-width: 0;
`;

const StyledMetadataSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
`;

const StyledLinkBoundary = styled.span`
  min-width: 0;
`;

const StyledMetadataLink = styled(AgentLinkButton)`
  max-width: 240px;

  && {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
    text-decoration-line: underline;
    text-decoration-style: dotted;
  }
`;

const StyledTrailingActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledTimingLabel = styled(MDSText)`
  white-space: nowrap;

  @media (max-width: 720px) {
    display: none;
  }
`;

const StyledMenuBoundary = styled.div`
  flex: 0 0 auto;
`;
